import { useState, useCallback, RefObject } from "react";
import mapboxgl from "mapbox-gl";
import { mapStyles } from "../config/mapConfig";

type MapStyle = keyof typeof mapStyles;

interface MarkerData {
	element: HTMLElement;
	lngLat: mapboxgl.LngLat;
	popup?: mapboxgl.Popup;
}

interface BarrierData {
	id: string;
	coordinates: number[][];
	color: string;
}

export const useMapStyles = (
	map: RefObject<mapboxgl.Map | null>,
	mapInitialized: boolean
) => {
	const [currentStyle, setCurrentStyle] = useState<MapStyle>("streets");
	const [isStyleLoading, setIsStyleLoading] = useState(false);

	const getAllMarkers = useCallback(() => {
		if (!mapInitialized || !map.current) return { markers: [], barriers: [] };

		const markers: MarkerData[] = [];
		const barriers: BarrierData[] = [];

		// Get all markers
		map.current?._markers?.forEach((marker) => {
			const lngLat = marker.getLngLat();
			const element = marker._element;

			// Get popup if it exists
			let popup: mapboxgl.Popup | undefined;
			const popupElement = element.querySelector(".mapboxgl-popup");
			if (popupElement) {
				const content = popupElement.innerHTML;
				popup = new mapboxgl.Popup({ offset: 25 })
					.setLngLat(lngLat)
					.setHTML(content);
			}

			markers.push({
				element: element.cloneNode(true) as HTMLElement,
				lngLat,
				popup,
			});
		});

		// Get all barrier polygons
		const style = map.current.getStyle();
		if (!style) return { markers, barriers };

		const layers = style.layers || [];
		const sources = style.sources || {};

		layers.forEach((layer: any) => {
			if (layer.id.includes("barrier-") && layer.id.endsWith("-fill")) {
				const baseId = layer.id.replace("-fill", "");
				const source = sources[baseId];

				if (source && source.type === "geojson") {
					const data = (source as any).data;
					if (data.geometry.type === "Polygon") {
						barriers.push({
							id: baseId,
							coordinates: data.geometry.coordinates[0],
							color: layer.paint["fill-color"] || "#FF0000",
						});
					}
				}
			}
		});

		return { markers, barriers };
	}, [map, mapInitialized]);

	const restoreMarkers = useCallback(
		({
			markers,
			barriers,
		}: {
			markers: MarkerData[];
			barriers: BarrierData[];
		}) => {
			if (!map.current) return;

			// Restore markers
			markers.forEach(({ element, lngLat, popup }) => {
				const marker = new mapboxgl.Marker({
					element: element.cloneNode(true) as HTMLElement,
					draggable: element.className.includes("personal-space-marker"),
				})
					.setLngLat(lngLat)
					.addTo(map.current!);

				if (popup) {
					marker.setPopup(popup);
				}
			});

			// Restore barriers
			barriers.forEach(({ id, coordinates, color }) => {
				try {
					// Check if source already exists
					if (!map.current?.getSource(id)) {
						// Add source
						map.current?.addSource(id, {
							type: "geojson",
							data: {
								type: "Feature",
								geometry: {
									type: "Polygon",
									coordinates: [coordinates],
								},
								properties: {
									color,
								},
							},
						});
					}

					// Check if layers exist before adding them
					if (!map.current?.getLayer(`${id}-fill`)) {
						// Add fill layer
						map.current?.addLayer({
							id: `${id}-fill`,
							type: "fill",
							source: id,
							paint: {
								"fill-color": color,
								"fill-opacity": 0.3,
							},
						});
					}

					if (!map.current?.getLayer(`${id}-line`)) {
						// Add line layer
						map.current?.addLayer({
							id: `${id}-line`,
							type: "line",
							source: id,
							paint: {
								"line-color": color,
								"line-width": 2,
							},
						});
					}
				} catch (error) {
					console.error(`Error restoring barrier ${id}:`, error);
				}
			});
		},
		[map]
	);

	const changeMapStyle = useCallback(
		(styleName: MapStyle) => {
			if (!map.current || currentStyle === styleName) return;

			setIsStyleLoading(true);
			const style = mapStyles[styleName];

			// Store all markers and barriers before style change
			const markersAndBarriers = getAllMarkers();

			// Remove existing markers
			document.querySelectorAll(".mapboxgl-marker").forEach((el) => {
				el.remove();
			});

			// Remove existing barrier layers and sources
			if (map.current) {
				const layers = (map.current as any).getStyle().layers || [];
				layers.forEach((layer: any) => {
					const id = layer.id;
					if (id.includes("barrier-")) {
						map.current?.removeLayer(id);
						// Only remove source for fill layers to avoid removing twice
						if (id.endsWith("-fill")) {
							map.current?.removeSource(id.replace("-fill", ""));
						}
					}
				});
			}

			// Set up one-time event listener for style load
			const onStyleData = () => {
				restoreMarkers(markersAndBarriers);
				setIsStyleLoading(false);

				try {
					map.current?.off("styledata", onStyleData);
				} catch (error) {
					console.error(
						"Error removing styledata event listener:",
						(error as Error).message
					);
				}
			};

			map.current.on("styledata", onStyleData);
			map.current.setStyle(style);
			setCurrentStyle(styleName);
		},
		[map, currentStyle, getAllMarkers, restoreMarkers]
	);

	return {
		currentStyle,
		isStyleLoading,
		changeMapStyle,
		availableStyles: Object.keys(mapStyles) as MapStyle[],
	};
};
